import React, { useMemo } from "react";
import { Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { getOrParseDateTime } from "@cpt/shared";
import type { OrderHistoryEntryDto } from "client/api-v2";
import { OrderStatusChip } from "areas/orders/components/chrome/OrderStatusChip";

/**
 * An instant as the audit trail states it: to the minute, in UTC. The reader's own zone is
 * deliberately not used - two people comparing this record must read the same clock.
 * @param instant
 */
export function formatInstantInUtc(instant: string): string {
  return getOrParseDateTime(instant).toUTC().toFormat("yyyy-MM-dd HH:mm");
}

interface OrderVersionHistoryProps {
  history: OrderHistoryEntryDto[];
}

// The API numbers versions from 1 at creation, and it is a plain integer - not the sequential
// GUID that trips up an in-memory sort elsewhere in this project - so sorting on it directly
// carries no ordering trap. Newest first reads better than the creation order the API returns.
function byRevisionDescending(a: OrderHistoryEntryDto, b: OrderHistoryEntryDto): number {
  return b.revision - a.revision;
}

/**
 * Every version of the order, newest write first, from the record's temporal table
 * (`GetOrderHistoryRequest`).
 *
 * Four columns, not the five the approved design draws. The fifth, `Change`, holds a sentence
 * naming what a version changed - "Term changed 12 to 24 months", "Actual usage set to 4,130
 * Projects". The temporal record cannot produce one: it stores each version's own values, never a
 * diff against its neighbour, so the sentence needs a structured field-diff model that does not
 * exist yet. An empty column would read as a broken feature rather than an absent one, so the
 * column is left out until the diff model can fill it.
 *
 * Two versions can therefore be identical in all four columns and still be different versions.
 * Every version stays on screen regardless: the version number is what tells them apart, and the
 * caption says so, because a list that hid a row for looking redundant would be worthless as an
 * audit trail.
 * @param root0
 * @param root0.history
 */
export function OrderVersionHistory({ history }: OrderVersionHistoryProps) {
  const orderedHistory = useMemo(() => [...history].sort(byRevisionDescending), [history]);

  if (orderedHistory.length === 0) {
    return <Typography color="text.secondary">No history recorded yet.</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        This list shows when each version was written, by whom, and the status it left behind. It does not show what
        changed inside it, so two versions can read alike - the version number tells them apart.
      </Typography>
      {/* The table sets its own minimum width and scrolls inside this box, so a long author name
          cannot push the record rail beside it off the screen. */}
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 480 }}>
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>When (UTC)</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>Status after</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderedHistory.map((entry) => (
              <TableRow key={entry.revision}>
                <TableCell>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <Box component="span" sx={{ fontFamily: "monospace" }}>{`v${entry.revision}`}</Box>
                    {/* Which version the order is on now. It is the top row of a newest-first list,
                        so this states in words what the position only implies. */}
                    {entry.isCurrent && <Chip label="Current" size="small" color="primary" />}
                  </Stack>
                </TableCell>
                <TableCell>{formatInstantInUtc(entry.validFrom)}</TableCell>
                <TableCell>{entry.modifiedByUser}</TableCell>
                <TableCell>
                  <OrderStatusChip status={entry.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
}
